import React from 'react';
import PropTypes from 'prop-types';
import { defaults, has, values, chain } from 'underscore';
import { CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AdSummaryBar } from './ad-summary-bar';
import { AdGroupSummaryBar } from './ad-group-summary-bar';
import { CampaignSummaryBar } from './campaign-summary-bar';
import { ExperimentAdGroupSummaryBar } from './experiment-ad-group-summary-bar';
import { ExperimentCampaignSummaryBar } from './experiment-campaign-summary-bar';
import { SummaryBarFactory } from './components';
import { getSummaryBarStyle } from './styles';

export class SummaryBarInternal extends React.PureComponent {
  componentDidMount() {
    if (this.props.perfMarker) {
      this.props.perfMarker.done();
    }
  }

  render() {
    const {
      adGroup,
      updateAdGroup,
      campaign,
      osData,
      updateCampaign,
      campaignType,
      summaryBarActivityLogger,
      scope,
      siteMap,
      permissions,
      canEdit,
      skipInvalidateCache,
      updateExperimentCampaign,
    } = this.props;

    const summaryBarSharedProps = {
      summaryBarActivityLogger,
      scope,
      siteMap,
      permissions,
      canEdit,
      skipInvalidateCache,
      excludedLocationOnly: chain(campaign).result('bidStrategy').value() === 'Commission',
    };
    let currentSummaryBar = i18n => (
      <SummaryBarFactory
        breadcrumbsProps={{ campaignType }}
        i18n={i18n}
        summaryBarActivityLogger={summaryBarActivityLogger}
        scope={scope}
        siteMap={siteMap}
      />
    );

    if (adGroup) {
      if (has(scope.values, 'adId')) {
        currentSummaryBar = i18n => (
          <AdSummaryBar
            adGroup={defaults({ campaignType }, adGroup)}
            updateAdGroup={updateAdGroup}
            i18n={i18n}
            {...summaryBarSharedProps}
          />
        );
      } else {
        currentSummaryBar = i18n => (adGroup.isExperiment ?
          (<ExperimentAdGroupSummaryBar
            adGroup={defaults({ campaignType }, adGroup)}
            updateAdGroup={updateAdGroup}
            i18n={i18n}
            {...summaryBarSharedProps}
          />) :
          (<AdGroupSummaryBar
            adGroup={defaults({ campaignType }, adGroup)}
            updateAdGroup={updateAdGroup}
            i18n={i18n}
            {...summaryBarSharedProps}
          />));
      }
    } else if (campaign) {
      currentSummaryBar = i18n => (campaign.isExperiment ?
        (<ExperimentCampaignSummaryBar
          campaign={defaults({ campaignType }, campaign)}
          i18n={i18n}
          updateExperimentCampaign={updateExperimentCampaign}
          {...summaryBarSharedProps}
        />) :
        (<CampaignSummaryBar
          campaign={defaults({ campaignType }, campaign)}
          osData={osData}
          updateCampaign={updateCampaign}
          i18n={i18n}
          {...summaryBarSharedProps}
        />));
    }

    return (
      <I18nContext.Consumer>
        {i18n => currentSummaryBar(i18n)}
      </I18nContext.Consumer>
    );
  }
}

SummaryBarInternal.propTypes = {
  adGroup: PropTypes.shape({
    isExperiment: PropTypes.bool,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    campaignId: PropTypes.number.isRequired,
    campaignName: PropTypes.string.isRequired,
    currentCampaignType: PropTypes.oneOf(values(CampaignType)).isRequired,
    campaignSubType: PropTypes.string,
    status: PropTypes.string,
    bid: PropTypes.number.isRequired,
    bidStrategy: PropTypes.string.isRequired,
    locations: PropTypes.array,
    audiences: PropTypes.array,
    devices: PropTypes.array,
  }),
  updateAdGroup: PropTypes.func,
  campaign: PropTypes.shape({
    isExperiment: PropTypes.bool,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string,
    currentCampaignType: PropTypes.oneOf(values(CampaignType)).isRequired,
    campaignSubType: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    originalCampaignName: PropTypes.string,
    originalCampaignId: PropTypes.number,
    budget: PropTypes.shape({}).isRequired,
    bidStrategy: PropTypes.string.isRequired,
    locations: PropTypes.array,
    audiences: PropTypes.array,
    devices: PropTypes.array,
    experimentId: PropTypes.number,
    hasBudgetLandscape: PropTypes.bool,
  }),
  osData: PropTypes.shape({
    OverallOptimizationScoreBar: PropTypes.exact({
      Available: PropTypes.number,
      Dismissed: PropTypes.number,
      Gained: PropTypes.number,
    }),
  }),
  updateCampaign: PropTypes.func,
  updateExperimentCampaign: PropTypes.func,
  campaignType: PropTypes.oneOf(values(CampaignType)).isRequired,
  canEdit: PropTypes.bool,
  perfMarker: PropTypes.shape({
    done: PropTypes.func.isRequired,
  }),
  summaryBarActivityLogger: PropTypes.shape({
    changeScopeActivity: PropTypes.any,
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  siteMap: siteMapPropType.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  skipInvalidateCache: PropTypes.bool,
};

SummaryBarInternal.defaultProps = {
  adGroup: null,
  updateAdGroup: null,
  campaign: null,
  osData: null,
  updateCampaign: null,
  updateExperimentCampaign: null,
  canEdit: false,
  perfMarker: null,
  skipInvalidateCache: false,
};

export const SummaryBar = withDefaultStyles(
  SummaryBarInternal,
  getSummaryBarStyle
);
